// Module 1 — typed PlayerError helpers for the player error pipeline.
//
// Maps HTMLVideoElement `MediaError` codes and hls.js error data into a
// uniform `PlayerError` shape that parent components use to decide between
// silent retry, optional fallback, and fatal surface.

export type PlayerErrorKind =
    | 'media-network'        // MediaError code 2, hls.js NETWORK_ERROR
    | 'media-decode'         // MediaError code 3, hls.js MEDIA_ERROR
    | 'media-not-supported'  // MediaError code 4
    | 'media-aborted'        // MediaError code 1 (user-driven)
    | 'channel-offline'      // provider returned a static/offline live placeholder
    | 'provider-stalled'     // provider's live manifest froze or returned a non-HLS body (our proxy's 504/502 verdict)
    | 'stream-limit'         // provider concurrent-stream cap (HTTP 458/429/509 upstream)
    | 'session-lost'         // live-buffer-resilience: fatal networkError, HTTP 403 — the playback registry entry is gone (TTL sweep / buffer torn down)
    | 'admin-stopped'        // An administrator explicitly ended this playback session.
    | 'rewind-rebased'       // live-buffer disk policy released old rewind media; reload at the advertised safe point
    | 'load-timeout'         // 30s elapsed without loadedmetadata
    | 'startup-no-frame'     // live attachment did not render a frame before the startup deadline
    | 'stall-timeout'        // 20s buffering stall without playing
    | 'unknown';

export interface PlayerError {
    kind: PlayerErrorKind;
    /** Plain-English user-facing message. No URLs, tokens, or codes. */
    message: string;
    /** True when the caller may retry or offer an alternate playback path. */
    recoverable: boolean;
    /** True when the spinner should clear and a sticky toast should appear. */
    fatal: boolean;
    /** Provider cooldown hint, when the playback response includes Retry-After. */
    retryAfterSec?: number;
    /** Raw error object for structured logging — never rendered to the user. */
    raw?: unknown;
}

// User-facing message strings — plain English, no codes or URLs.
const MESSAGES: Record<PlayerErrorKind, string> = {
    'media-network':
        'Could not load this video. The source may be unreachable or blocked.',
    'media-decode': 'Could not decode this video. Retry playback or use the external-player button.',
    'media-not-supported': 'This video format is not supported by your browser.',
    'media-aborted': 'Playback was cancelled.',
    // Same copy as userMessages.ts's ERROR_KIND_MESSAGES.channel_offline.
    // This module must stay a zero-import Node-loadable leaf
    // (tests/unit/streamLimitPair.test.js require()s it directly), so the
    // literal is duplicated here and PINNED by the pair assertion in
    // web/src/lib/userMessages.test.ts — drift fails that test.
    'channel-offline':
        'This channel appears to be offline — the provider is serving a placeholder instead of a live stream.',
    // Names the culprit on purpose. This class of fault used to surface as
    // the generic 'media-network' copy ("the source may be unreachable or
    // blocked"), which left the viewer unable to tell a provider outage from
    // their own connection — the 2026-08-21 diagnosis, where a provider
    // packager restart showed up as bare stall warnings for 3 minutes.
    // Deliberately does NOT say "retrying…", unlike the live-buffer chip's
    // copy. On the direct-proxy path nothing retries by the time this
    // renders: the hls.js error is fatal (its own retries are spent) and
    // `retuneActionForErrorKind` leaves this kind at 'none' on purpose,
    // because re-tuning into a stalled provider just burns another upstream
    // connection. The toast carries a manual Retry action instead (App.vue's
    // `retryAction` gate) — promising an automatic retry the code does not
    // perform is exactly the kind of lie this copy exists to remove.
    'provider-stalled':
        'Your provider stopped sending new video for this channel. The problem is on their end, not your connection.',
    'stream-limit':
        'Stream limit reached — your provider rejected this connection. Wait a moment, then try again.',
    // Deliberately does NOT say "Reconnecting…", for the same reason
    // 'provider-stalled' does not: this string is only ever RENDERED on the
    // surfaces where nothing is reconnecting. The live shell's single
    // reacquisition (App.vue `maybeRecoverLiveSession`) CONSUMES the error and
    // never shows this message — it either succeeds silently or surfaces the
    // real blocker instead. Everywhere this copy actually reaches a viewer,
    // recovery has already been declined or is not attempted at all:
    //   - live in a mode `isSessionBackedLiveMode` excludes (remux / catchup
    //     — `proxy` joined the session-backed set in Phase 1b), which falls
    //     through to the terminal toast;
    //   - VOD and recording playback in Watch.vue, where an
    //     `alreadyTranscoding` fatal takes the terminal panel branch.
    // It used to promise a reconnect on all of those. `canOfferRetry` returns
    // true for this kind so both surfaces carry a manual Retry, which is the
    // action that actually starts a new session.
    'session-lost':
        'Your playback session ended.',
    'admin-stopped': 'Playback stopped by an administrator.',
    'rewind-rebased':
        'Older rewind was released to keep live playback fluid.',
    'load-timeout': 'Video failed to load (timeout).',
    'startup-no-frame': 'Live video did not start.',
    // Reports the symptom, not a recovery promise: Watch owns any transcode
    // fallback and announces that action only when it actually starts.
    //
    // It read "Playback stalled. Waiting for video…" until Phase 5a added a
    // Retry button to this toast — at which point the copy was promising a
    // wait on a surface whose only remaining action is the user's. That is
    // the repo's "copy must not promise behaviour the code does not perform"
    // rule, arrived at from the opposite direction: the button changed, and
    // the sentence did not (2026-09-07 review, SUSPECTED #1).
    'stall-timeout': 'Playback stalled — no new video arrived.',
    unknown: 'Unexpected playback error.',
};

/**
 * Provider limit-class HTTP statuses ("max connections reached"). 458 is the
 * Xtream/Flussonic max-connections code (observed live on the real provider,
 * 2026-06-11); 513 was observed from NewEras under account/cooldown pressure;
 * 429/509 are the other limit codes panels emit. Mirror of
 * `server/services/streamLimit.js` `isStreamLimitStatus` — keep in sync.
 */
const STREAM_LIMIT_STATUSES = new Set([429, 458, 509, 513]);
const CHANNEL_OFFLINE_STATUS = 410;
/**
 * live-buffer-resilience — the playback registry entry backing this URL is
 * gone (TTL sweep, or the buffer was torn down): the token-authed request
 * gets a 403 from the browser-facing player-error path. Distinct from a
 * plain 401 (auth) or 410 (channel-offline) — this is "the SESSION died",
 * not "the channel died" or "you're logged out". A 404 in the same context
 * stays classified as `media-network` (unchanged).
 */
const SESSION_LOST_STATUS = 403;
// The composed live-buffer route uses this exact status/header pair when a
// browser asks for a segment released by the newly advanced rewind floor.
// A bare 409 must never trigger a player seek.
const REWIND_REBASED_STATUS = 409;

/**
 * `X-Nodecast-Playback-Error` values that mean "the PROVIDER's live stream
 * broke", set by `server/routes/proxy.js` alongside its 504 / 502. Both are
 * one user-facing story — the provider stopped delivering usable video — so
 * they collapse to a single kind. Mirror of the two `res.set(...)` sites in
 * proxy.js; keep in sync.
 */
const PROVIDER_STALL_PLAYBACK_ERRORS: ReadonlySet<string> = new Set([
    'manifest_not_advancing',
    'invalid_manifest_body',
]);

/**
 * `X-Nodecast-Playback-Error` values that mean the SERVER-SIDE playback
 * identity is gone — set by proxy mode's registry-liveness gate (Phase 1b).
 *
 * The 403 STATUS branch below already produces `session-lost` for these, so
 * this mapping is not what makes proxy recovery work today. It exists so the
 * header is self-sufficient: Phase 4 reads
 * `X-Nodecast-Playback-Error` off `networkDetails` for error classes where
 * hls.js does not surface a usable status, and a value the classifier does not
 * recognise falls through to the generic "source may be unreachable" copy.
 * `tests/unit/playbackErrorHeaderPair.test.js` is what enforces that every
 * value the server emits lands somewhere deliberate.
 */
const SESSION_LOST_PLAYBACK_ERRORS: ReadonlySet<string> = new Set([
    'session_expired',
]);

/** See `server/routes/rewind.js`'s shared segment route. */
const REWIND_REBASED_PLAYBACK_ERRORS: ReadonlySet<string> = new Set([
    'rewind_rebased',
]);
const ADMIN_STOPPED_PLAYBACK_ERRORS: ReadonlySet<string> = new Set(['admin_force_stop']);

/** The one header the server uses to diagnose a playback fault. */
export const PLAYBACK_ERROR_HEADER = 'X-Nodecast-Playback-Error';

/**
 * The proxy's own diagnosis of an upstream fault.
 *
 * **Phase 4 (playback-reliability-prod-incident-2026-09-03.md): this used to
 * read `data.response.headers`, which does not exist.** Verified against the
 * installed hls.js 1.6.16: `LoaderResponse` is declared as
 * `{ url, data?, code?, text? }` (`node_modules/hls.js/src/types/loader.ts:64-71`)
 * — there is no `headers` field, and nothing in hls.js ever populates one. So
 * the previous parser was a dead production path that could only ever return
 * `null`, and every classification that depended on it silently fell through
 * to the generic "source may be unreachable" copy.
 *
 * The headers exist on the TRANSPORT object. hls.js's default loader is
 * `XhrLoader`, and on failure it passes the `XMLHttpRequest` itself as the
 * third `onError` argument (`src/utils/xhr-loader.ts:250-254`), which reaches
 * listeners as `data.networkDetails`. `getResponseHeader()` is therefore the
 * only way to read the header, and no custom loader is needed.
 *
 * Fails CLOSED by design: a non-XHR `networkDetails` (a fetch-loader build, a
 * custom loader, or a synthetic error hls.js raised with no transport at all)
 * returns `null` rather than guessing. A wrong diagnosis is worse than none —
 * it would route the viewer to a specific, confidently wrong message.
 */
export function proxyPlaybackError(data: HlsErrorData): string | null {
    const details = data.networkDetails;
    if (typeof XMLHttpRequest === 'undefined') return null;
    if (!(details instanceof XMLHttpRequest)) return null;
    try {
        // Header names are case-insensitive per RFC 9110 §5.1 and
        // `getResponseHeader` implements that, so one spelling is enough —
        // unlike a plain object lookup, which is why the old parser checked
        // two.
        return details.getResponseHeader(PLAYBACK_ERROR_HEADER) || null;
    } catch {
        // A destroyed/unsent XHR throws INVALID_STATE_ERR in some browsers.
        return null;
    }
}

/**
 * Parse Retry-After's delta-seconds or HTTP-date form. Return null for absent
 * or malformed values; a date already in the past is a valid zero-second hint.
 */
export function parseRetryAfterSec(value: string | null | undefined, nowMs = Date.now()): number | null {
    if (typeof value !== 'string') return null;
    const header = value.trim();
    if (!header) return null;
    if (/^\d+(?:\.\d+)?$/.test(header)) {
        const seconds = Number(header);
        return Number.isFinite(seconds) ? seconds : null;
    }
    const dateMs = Date.parse(header);
    if (!Number.isFinite(dateMs)) return null;
    return Math.max(0, Math.ceil((dateMs - nowMs) / 1000));
}

/** Read Retry-After from the hls.js transport without trusting its shape. */
function retryAfterSecFromNetworkDetails(details: unknown): number | null {
    if (!details || typeof details !== 'object') return null;
    try {
        const transport = details as {
            getResponseHeader?: (name: string) => string | null;
            headers?: { get?: (name: string) => string | null };
        };
        const raw = typeof transport.getResponseHeader === 'function'
            ? transport.getResponseHeader('Retry-After')
            : typeof transport.headers?.get === 'function'
                ? transport.headers.get('Retry-After')
                : null;
        return parseRetryAfterSec(raw);
    } catch {
        return null;
    }
}

export function isStreamLimitStatus(status: number | null | undefined): boolean {
    return status != null && STREAM_LIMIT_STATUSES.has(status);
}

/**
 * Map an `HTMLVideoElement.error` (`MediaError`) to a `PlayerError`.
 * Handles null / undefined gracefully (returns kind 'unknown').
 */
export function fromMediaError(e: MediaError | null | undefined): PlayerError {
    if (!e) {
        return { kind: 'unknown', message: MESSAGES.unknown, recoverable: false, fatal: true, raw: e };
    }
    switch (e.code) {
        case 1: // MEDIA_ERR_ABORTED — usually source swap / user cancel; log-only.
            return { kind: 'media-aborted', message: MESSAGES['media-aborted'], recoverable: false, fatal: false, raw: e };
        case 2: // MEDIA_ERR_NETWORK
            return { kind: 'media-network', message: MESSAGES['media-network'], recoverable: true, fatal: true, raw: e };
        case 3: // MEDIA_ERR_DECODE
            return { kind: 'media-decode', message: MESSAGES['media-decode'], recoverable: true, fatal: true, raw: e };
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
            return { kind: 'media-not-supported', message: MESSAGES['media-not-supported'], recoverable: true, fatal: true, raw: e };
        default:
            return { kind: 'unknown', message: MESSAGES.unknown, recoverable: false, fatal: true, raw: e };
    }
}

/**
 * Minimum shape of hls.js error data we depend on. Typed loosely so this
 * module doesn't require hls.js as a compile-time import (it's lazy-loaded
 * via Vidstack's `provider.library = () => import('hls.js')`).
 */
export interface HlsErrorData {
    type: string;
    details?: string;
    fatal: boolean;
    reason?: string;
    /**
     * hls.js network-error payload — `code` is the HTTP status of the failed
     * request. `LoaderResponse` in hls.js 1.6.16 is `{url, data?, code?, text?}`
     * and has NO `headers` field; see `proxyPlaybackError` for where the
     * headers actually live.
     */
    response?: { code?: number; text?: string };
    /**
     * The transport object for the failed request. With hls.js's default
     * loader this is the `XMLHttpRequest`
     * (`src/utils/xhr-loader.ts:250-254`), which is the only place response
     * headers are reachable. Typed `unknown` on purpose: `proxyPlaybackError`
     * narrows it and fails closed on anything else.
     */
    networkDetails?: unknown;
    /**
     * hls.js's OWN retry verdict, attached by its `ErrorController` before the
     * ERROR event reaches listeners (hls.js 1.6.16 `error-controller.ts`'s
     * `IErrorAction`). Read for Phase 0B measurement only — nothing in this
     * module classifies on it. `action` is a `const enum` in hls.js, so it
     * arrives as a plain number; `lib/player/noProgress.ts` names it.
     */
    errorAction?: {
        action?: number;
        retryCount?: number;
        resolved?: boolean;
        retryConfig?: { maxNumRetry?: number } | null;
    } | null;
}

/**
 * Map hls.js `Hls.Events.ERROR` data to a `PlayerError`.
 *
 * Non-fatal hls.js errors are real events (ABR level switch, partial segment
 * retry) — they should not surface to the user. Non-fatal returns
 * `fatal: false, recoverable: false` as a signal for the caller to log-only.
 */
export function fromHlsErrorData(data: HlsErrorData): PlayerError {
    if (!data.fatal) {
        return { kind: 'unknown', message: MESSAGES.unknown, recoverable: false, fatal: false, raw: data };
    }
    switch (data.type) {
        case 'networkError':
            if (ADMIN_STOPPED_PLAYBACK_ERRORS.has(proxyPlaybackError(data) ?? '')) {
                return { kind: 'admin-stopped', message: MESSAGES['admin-stopped'], recoverable: false, fatal: true, raw: data };
            }
            if (data.response?.code === CHANNEL_OFFLINE_STATUS) {
                return { kind: 'channel-offline', message: MESSAGES['channel-offline'], recoverable: false, fatal: true, raw: data };
            }
            // Provider concurrent-stream cap (e.g. Xtream 458 "max connections").
            // NOT recoverable: the external player / a transcode hits the same
            // account-level limit — the only fix is freeing the other stream.
            if (isStreamLimitStatus(data.response?.code)) {
                return {
                    kind: 'stream-limit',
                    message: MESSAGES['stream-limit'],
                    recoverable: false,
                    fatal: true,
                    retryAfterSec: retryAfterSecFromNetworkDetails(data.networkDetails) ?? undefined,
                    raw: data,
                };
            }
            // Session-lost (403): the registry entry is gone — a plain
            // re-tune re-mints a fresh session. Recoverable (auto-retune
            // handles it), unlike a 404/other network fault which now falls
            // through to the sticky-toast path (retunePolicy.ts).
            if (data.response?.code === SESSION_LOST_STATUS) {
                return { kind: 'session-lost', message: MESSAGES['session-lost'], recoverable: true, fatal: true, raw: data };
            }
            if (
                data.response?.code === REWIND_REBASED_STATUS
                && REWIND_REBASED_PLAYBACK_ERRORS.has(proxyPlaybackError(data) ?? '')
            ) {
                return {
                    kind: 'rewind-rebased',
                    message: MESSAGES['rewind-rebased'],
                    recoverable: true,
                    fatal: true,
                    raw: data,
                };
            }
            // Checked LAST of the network branches so every existing
            // classification above keeps exact precedence: the proxy only
            // sets this header on its own 502/502-class verdicts, none of
            // which are 410/458/403, so this cannot shadow them.
            //
            // Recoverable (the provider usually comes back within a minute)
            // but `retuneActionForErrorKind` leaves this kind at its `'none'`
            // default — auto-retuning into a stalled provider just burns
            // another upstream connection. Same outcome as the
            // 'media-network' this used to be classified as; only the
            // message the viewer reads changes.
            {
                const proxyError = proxyPlaybackError(data);
                if (proxyError !== null && SESSION_LOST_PLAYBACK_ERRORS.has(proxyError)) {
                    return {
                        kind: 'session-lost',
                        message: MESSAGES['session-lost'],
                        recoverable: true,
                        fatal: true,
                        raw: data,
                    };
                }
                if (proxyError !== null && PROVIDER_STALL_PLAYBACK_ERRORS.has(proxyError)) {
                    return {
                        kind: 'provider-stalled',
                        message: MESSAGES['provider-stalled'],
                        recoverable: true,
                        fatal: true,
                        raw: data,
                    };
                }
            }
            return { kind: 'media-network', message: MESSAGES['media-network'], recoverable: true, fatal: true, raw: data };
        case 'mediaError':
            return { kind: 'media-decode', message: MESSAGES['media-decode'], recoverable: true, fatal: true, raw: data };
        default:
            return { kind: 'unknown', message: MESSAGES.unknown, recoverable: false, fatal: true, raw: data };
    }
}

/**
 * Classify a failed live-session ACQUISITION — an API status from
 * `POST /api/playback/live-session`, not an hls.js event — into the same kinds
 * runtime playback errors use.
 *
 * Phase 0A (playback-reliability-prod-incident-2026-09-03): when a terminal
 * session loss is detected the shell consumes that error (its copy promises
 * "Reconnecting…") and requests ONE fresh session. If that request fails, the
 * user must be told what actually blocked it. Routing it through this function
 * rather than the generic API-error formatter keeps ONE phrasing per cause:
 * `userMessages.ts`'s `stream_limit` copy is written for the VOD PROBE path
 * ("so the probe was rejected"), which is a different and, here, untrue
 * sentence.
 *
 * Returns `null` for anything this vocabulary does not name, so the caller
 * keeps its own generic fallback rather than guessing.
 */
export function fromSessionAcquisitionStatus(status: number | null | undefined): PlayerError | null {
    if (status == null) return null;
    if (status === CHANNEL_OFFLINE_STATUS) {
        return { kind: 'channel-offline', message: MESSAGES['channel-offline'], recoverable: false, fatal: true };
    }
    if (isStreamLimitStatus(status)) {
        return { kind: 'stream-limit', message: MESSAGES['stream-limit'], recoverable: false, fatal: true };
    }
    return null;
}

/**
 * The kinds whose terminal toast should carry a manual **Retry** action.
 *
 * Exported (rather than living inline in App.vue, where it did until
 * 2026-08-24) because it is a per-KIND affordance, which makes it invisibly
 * fragile: carving a new kind out of an existing one silently removes the
 * button that kind used to get. That is exactly what happened when
 * `provider-stalled` was split out of `media-network` — the split was
 * reviewed, tested, and type-checked, and still dropped Retry from a
 * provider outage, leaving "Find it elsewhere" (a navigation AWAY from the
 * channel) as the only offered action.
 *
 * Rationale per kind:
 *  - `media-network`   — no longer auto-retunes (retunePolicy.ts, the
 *                        2026-07-05 backward-jump fix), so the toast it falls
 *                        through to needs a manual way back in.
 *  - `stream-limit`    — the player store's bounded automatic retry is spent
 *                        by the time this surfaces; re-tuning once the other
 *                        device frees the slot is the actual remedy.
 *  - `provider-stalled` — nothing retries automatically (fatal hls.js error,
 *                        and retuneActionForErrorKind is 'none' on purpose),
 *                        and these outages typically clear inside a minute,
 *                        so Retry is the single most likely action to work.
 *  - `session-lost`    — by the time this string is RENDERED, the automatic
 *                        reacquisition has been declined (a live mode
 *                        `isSessionBackedLiveMode` excludes — remux or
 *                        catchup; `proxy` became session-backed in Phase 1b),
 *                        or was never on offer (VOD / recording in
 *                        Watch.vue), or it ran and failed. Requesting a
 *                        fresh session is exactly the remedy, and it is the
 *                        user's only way to ask for one — so the copy stops
 *                        promising a reconnect and the button performs it.
 *  - `load-timeout` /
 *    `stall-timeout`    — ADDED in Phase 5, from the Phase 0B screenshot
 *                        evidence. The captures put the two failure toasts
 *                        side by side and the inconsistency was only visible
 *                        in the picture: a terminal `403` offered *Retry* and
 *                        *Find it elsewhere*, while the 30 s load timeout
 *                        offered only *Find it elsewhere* — so the case with
 *                        the BETTER chance of recovering was the one with no
 *                        retry affordance. A timeout means the stream never
 *                        started or stopped advancing; both are transient far
 *                        more often than not, and re-tuning is precisely what
 *                        a user does by hand anyway (by switching away and
 *                        back). Offering the button saves that detour.
 */
const RETRY_ELIGIBLE_KINDS: ReadonlySet<PlayerErrorKind> = new Set([
    'media-network',
    'media-decode',
    'stream-limit',
    'provider-stalled',
    'session-lost',
    'load-timeout',
    'startup-no-frame',
    'stall-timeout',
]);

/** Whether a terminal playback error's toast should offer a manual Retry. */
export function canOfferRetry(kind: PlayerErrorKind): boolean {
    return RETRY_ELIGIBLE_KINDS.has(kind);
}

/**
 * The kinds an external player can actually do something about: the browser
 * could not DECODE what the provider sent, and a native player with wider
 * codec support might. Everything else — a stream-limit rejection, a dead
 * upstream, a lost session — hits the same wall outside the browser.
 */
const EXTERNAL_PLAYER_CAN_HELP: ReadonlySet<PlayerErrorKind> = new Set([
    'media-decode',
    'media-not-supported',
]);

const EXTERNAL_PLAYER_ADVICE =
    'Browser playback failed. Use the external-player button for native playback.';

/**
 * The message a terminal playback error shows the user (field sweep B5,
 * fault 2).
 *
 * The advice used to be selected by `err.recoverable`, which is a different
 * question — it means "the caller may retry or offer another path", not "your
 * browser could not decode this". A provider stream-limit hop therefore
 * offered the external-player button, which cannot work: the limit is on the
 * account upstream, so a native player consumes the same capped connection.
 * Kind is the honest axis; every other kind already carries a message that
 * names its own cause.
 */
export function playbackErrorToastMessage(err: PlayerError): string {
    return EXTERNAL_PLAYER_CAN_HELP.has(err.kind) ? EXTERNAL_PLAYER_ADVICE : err.message;
}

/** Fired when `loadedmetadata` has not arrived within 30 seconds. */
export function loadTimeout(): PlayerError {
    return {
        kind: 'load-timeout',
        message: MESSAGES['load-timeout'],
        recoverable: true,
        fatal: true,
    };
}

/** Fired when live playback has metadata but never renders a video frame. */
export function startupNoFrameTimeout(): PlayerError {
    return {
        kind: 'startup-no-frame',
        message: MESSAGES['startup-no-frame'],
        recoverable: true,
        fatal: true,
    };
}

/** Fired when the video has been stalled (buffering) for 20 seconds. */
export function stallTimeout(): PlayerError {
    return {
        kind: 'stall-timeout',
        message: MESSAGES['stall-timeout'],
        recoverable: true,
        fatal: true,
    };
}
